/**
 * useFormDirty
 *
 * Tracks whether a form has unsaved changes and guards the close action.
 *
 * Usage – create forms (mark dirty on any field change):
 *   const { markDirty, reset: resetDirty, guardClose } = useFormDirty();
 *   watch(form, () => markDirty(), { deep: true });
 *   // reset after opening / after successful submit:
 *   resetDirty();
 *   // guard every close path:
 *   async function handleClose() {
 *     if (!(await guardClose())) return;
 *     resetDirty();
 *     emit('update:visible', false);
 *   }
 *
 * Usage – edit forms (only dirty when something actually changed):
 *   const snapshot = ref<Record<string, unknown> | null>(null);
 *   // after populating the form from server data:
 *   snapshot.value = JSON.parse(JSON.stringify(form.value));
 *   resetDirty();
 *   // watch & compare:
 *   watch(form, (cur) => {
 *     if (!snapshot.value) return;
 *     markDirty(JSON.stringify(cur) !== JSON.stringify(snapshot.value));
 *   }, { deep: true });
 */
import { ref, readonly } from 'vue';
import { useLocale } from '@/shared/composables/useLocale';
import { useConfirmDialog } from '@tms-phoenix/ui-vue';

export function useFormDirty() {
  const _isDirty = ref(false);
  const { t } = useLocale();
  const { confirmState, requestConfirm, handleConfirm, handleCancel, handleVisibleUpdate } = useConfirmDialog();

  /** Manually set the dirty flag. Pass false to clear it. */
  function markDirty(dirty = true) {
    _isDirty.value = dirty;
  }

  /** Clear the dirty flag (call after successful submit or when the dialog opens). */
  function reset() {
    _isDirty.value = false;
  }

  /**
   * Shows a confirmation dialog when the form has unsaved changes.
   *
   * Returns `true`  → safe to proceed with close (either clean or user chose Discard).
   * Returns `false` → user chose Keep Editing; caller should abort the close.
   */
  async function guardClose(): Promise<boolean> {
    if (!_isDirty.value) return true;
    return requestConfirm({
      title: t('common.discardChanges'),
      message: t('formDirty.message'),
      confirmLabel: t('mother.discard'),
      cancelLabel: t('mother.keepEditing'),
    });
  }

  return {
    isDirty: readonly(_isDirty),
    markDirty,
    reset,
    guardClose,
    // Expose for inline template confirm dialog (avoids global z-index issues)
    confirmState,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
    onVisibleUpdate: handleVisibleUpdate,
  };
}
